// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL IDIOMA DE QUIEN TIENE LA SESIÓN (B-2259, t523, 27-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Va dentro de la sesión (App.jsx, junto a TemaDeLaSesion) y no pinta nada. Con la función «idiomas» encendida para su
//  clínica, pregunta al servidor el idioma de esa persona (el suyo, si no el de la clínica, si no el castellano:
//  GET /api/idiomas/sesion), lo recuerda en este dispositivo y, si no es el que hay puesto, RECARGA LA PÁGINA UNA VEZ:
//  main.jsx carga el diccionario antes de pintar (utils/i18nCargar · prepararIdiomaGuardado), así que todo sale ya en
//  ese idioma, también lo que se pintó antes de saberlo. Recargar y no volver a montar a mano: la aplicación tiene
//  relojes, avisos y ventanas por fuera de las rutas, y una recarga los deja a todos en el idioma nuevo sin excepciones.
//
//  Con la función APAGADA (o sin sesión) no pregunta nada; si este dispositivo recordaba otro idioma, lo olvida y vuelve
//  al castellano. Una recarga automática como mucho cada 15 s (sessionStorage): nunca un bucle.
import { useEffect, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useEstadoFuncion } from '../../hooks/useFuncion'
import api from '../../services/api'
import { idiomaActivo } from '../../utils/i18n'
import { recordarIdioma, idiomaRecordado } from '../../utils/i18nCargar'

const CLAVE_RECARGA = 'biango-idioma-recarga'

export function recargarParaElIdioma(ventana = (typeof window !== 'undefined' ? window : null)) {
  if (!ventana || !ventana.location) return false
  try {
    const antes = Number(ventana.sessionStorage.getItem(CLAVE_RECARGA) || 0)
    if (Date.now() - antes < 15000) return false
    ventana.sessionStorage.setItem(CLAVE_RECARGA, String(Date.now()))
  } catch (_) { /* sin almacenamiento: se recarga igual, una vez */ }
  ventana.location.reload()
  return true
}

// Quien pulsa un idioma quiere verlo ya, aunque acabe de cambiarlo: esta no espera los 15 s, y los cuenta para que la automática no repita justo después.
export function recargarPorSuEleccion(ventana = (typeof window !== 'undefined' ? window : null)) {
  if (!ventana || !ventana.location) return false
  try { ventana.sessionStorage.setItem(CLAVE_RECARGA, String(Date.now())) } catch (_) { /* sin almacenamiento: se recarga igual */ }
  ventana.location.reload()
  return true
}

export default function IdiomaDeLaSesion() {
  const { user, loading } = useAuth()
  const { encendida, sabida } = useEstadoFuncion('idiomas')
  const id = user?.id || null
  const habiaSesion = useRef(false)
  useEffect(() => {
    if (loading) return
    if (!id) {
      // Al salir, se olvida: la siguiente persona de este ordenador entra en castellano hasta que se sepa el suyo.
      if (habiaSesion.current) recordarIdioma(null)
      habiaSesion.current = false
      return
    }
    habiaSesion.current = true
    if (!sabida) return
    if (!encendida) {
      if (idiomaRecordado()) recordarIdioma(null)
      if (idiomaActivo() !== 'es') recargarParaElIdioma()
      return
    }
    let vivo = true
    api.get('/idiomas/sesion').then((r) => {
      if (!vivo) return
      const idioma = (r && r.data && r.data.idioma) || 'es'
      recordarIdioma(idioma)
      if (idioma !== idiomaActivo()) recargarParaElIdioma()
    }).catch(() => { /* sin respuesta: se queda como está */ })
    return () => { vivo = false }
  }, [id, loading, encendida, sabida])
  return null
}
